import React, { useContext } from "react";
import { MenuContextProvider } from "./context/menuContext";
import useUncertainRef from "../hooks/useUncertainRef";
import DropdownMenuContext, {
  useDropdownMenuContext,
  DropdownMenuContextProvider,
} from "./context/dropdownMenuContext";
import useUniqueId from "../hooks/useUniqueId";
import { motion, AnimatePresence } from "framer-motion";
import { NewAnimatePresenceProps } from "../Form/FormItem";

export interface MenuProps {
  children?: any;
  classPrefix?: string;
  activeKey?: string;
  onSelect?: (key, e) => void;
  onKeyDown?: () => void;
  onToggle?: (_, e) => void;
  hidden?: boolean;
  placement?: string;
  menuClass?: string;
  className?: string;
}

const Menu = React.forwardRef<HTMLUListElement, MenuProps>((props, ref) => {
  const NewAnimatePresence: React.FC<NewAnimatePresenceProps> = AnimatePresence;

  const {
    children,
    classPrefix,
    activeKey,
    onSelect,
    onKeyDown,
    hidden,
    placement,
    menuClass,
    ...rest
  } = props;

  const menuRef = useUncertainRef(ref);
  const menuId = useUniqueId("menu-");
  const upperMenuControl = useContext(DropdownMenuContext);
  const menuControl = useDropdownMenuContext(menuRef);

  const getTransform = (deg) => {
    const rotate = `rotateX(${deg}deg)`;
    if (placement && placement.includes("center")) {
      return `${rotate} translateX(-50%)`;
    }
    return rotate;
  };

  const enterStyle = {
    opacity: 1,
    visibility: "visible" as const,
    transform: getTransform(0),
  };
  const exitStyle = {
    opacity: 0,
    visibility: "hidden" as const,
    transform: getTransform(40),
  };
  const initialStyle = exitStyle;

  return (
    <MenuContextProvider
      value={{
        activeKey,
        onSelect,
      }}
    >
      <DropdownMenuContextProvider value={menuControl}>
        <NewAnimatePresence exitBeforeEnter>
          {!hidden && (
            <motion.ul
              id={menuId}
              ref={menuRef}
              initial={initialStyle}
              animate={enterStyle}
              exit={exitStyle}
              transition={{ duration: 0.15, type: "tween" }}
              className={menuClass}
              {...rest}
            >
              {children}
            </motion.ul>
          )}
        </NewAnimatePresence>
      </DropdownMenuContextProvider>
    </MenuContextProvider>
  );
});

export default Menu;
